/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import type { SetRequired } from 'type-fest';

import { Environment } from '../../../../ts/assistant';
import { ReactivateSession } from '../../../../ts/session';
import { AsyncAppThunk, CreateAsyncThunk } from '../../../../ts/state';
import { listIntegrations } from '../../../channels/store/channels/actions';
import * as API from '../../api/environments';
import { getErrorString } from '../../util/errorHandler';
import {
  createdObjectEvents,
  deletedObjectEvents,
  updatedObjectEvents
} from '../../util/trackers/segment';
import { isCacheExpired } from '../helper';
import { selectIsNewIa } from '../instance/selectors';
import { addErrorMessage, addMessage, ToastStatus } from '../messages/actions';
import { resetWebChatForEnvironment } from '../onboarding/actions';
import { getAssistantId } from '../session/selectors';
import { listSkills } from '../skills/actions';
import { areEnvironmentsLoading, selectAllEnvironments } from './selectors';

interface ListEnvironmentsArgs {
  reactivateSession?: ReactivateSession;
}

export const listEnvironments = (args: ListEnvironmentsArgs = {}) =>
  listEnvironmentsWithArgs(args);

export const listEnvironmentsWithArgs = createAsyncThunk<
  Environment[],
  ListEnvironmentsArgs,
  CreateAsyncThunk
>(
  'environments/list',
  async ({ reactivateSession }, { dispatch, getState }) => {
    try {
      const assistantId = getAssistantId(getState());

      if (!assistantId) {
        return [];
      }

      const paginatedResults = await API.listEnvironments(
        assistantId,
        reactivateSession
      );

      return paginatedResults.environments;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    condition: (_params, { getState }) => {
      return selectIsNewIa(getState()) && !areEnvironmentsLoading(getState());
    }
  }
);

export const listEnvironmentsIfRequired = (): AsyncAppThunk<
  Promise<Environment[]>
> => {
  return async (dispatch, getState): Promise<Environment[]> => {
    const { listFetched } = getState()?.environments;
    // Avoid requests if data is not stale
    if (!isCacheExpired(listFetched)) {
      return Promise.resolve(selectAllEnvironments(getState()));
    }
    await dispatch(listEnvironments());
    return selectAllEnvironments(getState());
  };
};

type EnvironmentId = {
  assistantId: string;
  environmentId: string;
};

export const getEnvironment = createAsyncThunk<
  Environment,
  EnvironmentId,
  CreateAsyncThunk
>('environments/get', async (params, { dispatch }) => {
  try {
    return await API.getEnvironment(params.assistantId, params.environmentId);
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const createEnvironment = createAsyncThunk<
  Environment,
  SetRequired<Partial<Environment>, 'assistant_id'>,
  CreateAsyncThunk
>('environments/create', async (params, { dispatch }) => {
  const assistantId = params.assistant_id;
  let environmentId;
  try {
    const response = await API.createEnvironment(params);
    environmentId = response.environment_id;

    await dispatch(listIntegrations(assistantId)).unwrap();
    await dispatch(resetWebChatForEnvironment(environmentId));
    // After creating environment forcefully refresh the list by calling listEnvironments
    await dispatch(listEnvironments());

    // Creating an environment creates a search skill for it,
    // `listSkills()` will fetch the new skills
    await dispatch(listSkills());

    createdObjectEvents.environment(
      {
        object: environmentId,
        resultValue: 'success',
        successFlag: true
      },
      {
        waEnvironmentId: environmentId,
        waEnvironmentName: params.name
      }
    );
    await dispatch(
      addMessage({
        message: `Environment ${response.name} was created.`,
        status: ToastStatus.SUCCESS
      })
    );

    return response;
  } catch (error) {
    // Delete assistant if created
    if (environmentId) {
      await dispatch(deleteEnvironment({ assistantId, environmentId }));
    }

    dispatch(addErrorMessage(error));
    createdObjectEvents.environment({
      object: null,
      resultValue: getErrorString(error),
      successFlag: false
    });
    throw error;
  }
});

export const updateEnvironment = createAsyncThunk<
  Environment,
  SetRequired<Partial<Environment>, 'environment_id' | 'assistant_id'>,
  CreateAsyncThunk
>('environments/update', async (params, { dispatch }) => {
  try {
    const response = await API.updateEnvironment(params);
    updatedObjectEvents.environment(
      {
        object: params.environment_id,
        resultValue: 'success',
        successFlag: true
      },
      {
        waEnvironmentId: params.environment_id,
        sessionHistoryEnabled: params?.session_history?.enabled
      }
    );
    return response;
  } catch (error) {
    dispatch(addErrorMessage(error));
    updatedObjectEvents.environment(
      {
        object: params.environment_id,
        resultValue: getErrorString(error),
        successFlag: false
      },
      { waEnvironmentId: params.environment_id }
    );
    throw error;
  }
});

export const deleteEnvironment = createAsyncThunk<
  EnvironmentId,
  EnvironmentId,
  CreateAsyncThunk
>('environments/delete', async (params, { dispatch }) => {
  const { assistantId, environmentId } = params;

  try {
    await API.deleteEnvironment(assistantId, environmentId);
    // After deleting environment forcefully refresh the list by calling listEnvironments
    await dispatch(listEnvironments());

    deletedObjectEvents.environment(
      {
        object: environmentId,
        resultValue: 'success',
        successFlag: true
      },
      { waEnvironmentId: environmentId }
    );
    return params;
  } catch (error) {
    dispatch(addErrorMessage(error));
    deletedObjectEvents.environment({
      object: environmentId,
      waEnvironmentId: environmentId,
      resultValue: getErrorString(error),
      successFlag: false
    });
    throw error;
  }
});

export const resetAllEnvironments = createAction(
  'environments/resetAllEnvironments'
);
